import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const Campaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [leadsTables, setLeadsTables] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  
  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    // Check token on mount
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No authentication token found in localStorage');
      showSnackbar('Please log in to access this page', 'error');
    } else {
      fetchCampaigns();
      fetchLeadsTables();
      fetchAgents();
    }
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token available for API request');
        handleTokenExpiration();
        return;
      }
      
      const response = await fetch(`${API_URL}/api/campaigns`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Campaigns response status:', response.status);
      
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed - token expired or invalid');
        handleTokenExpiration();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('Campaigns API response:', data);
        
        // Handle different response formats
        let campaignsData = [];
        if (Array.isArray(data)) {
          campaignsData = data;
        } else if (data.campaigns && Array.isArray(data.campaigns)) {
          campaignsData = data.campaigns;
        } else {
          console.warn('Unexpected API response format for campaigns:', data);
          campaignsData = [];
        }
        
        setCampaigns(campaignsData);
      } else {
        showSnackbar('Failed to fetch campaigns', 'error');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showSnackbar('Error loading campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadsTables = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token available for API request');
        handleTokenExpiration();
        return;
      }
      
      console.log('Fetching leads tables with token present');
      
      const response = await fetch(`${API_URL}/api/leads/tables`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Leads tables response status:', response.status);
      
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed - token expired or invalid');
        handleTokenExpiration();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('Leads tables API response:', data);
        
        // Handle different response formats
        let tablesData = [];
        if (Array.isArray(data)) {
          tablesData = data;
        } else if (data.tables && Array.isArray(data.tables)) {
          tablesData = data.tables;
        } else {
          console.warn('Unexpected API response format for tables:', data);
          tablesData = [];
        }
        
        console.log('Processed leads tables:', tablesData);
        setLeadsTables(tablesData);
        
        if (tablesData.length === 0) {
          showSnackbar('No lead tables found. Please create one first.', 'warning');
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to fetch leads tables:', response.status, errorData);
        showSnackbar('Failed to fetch leads tables', 'error');
      }
    } catch (error) {
      console.error('Error fetching leads tables:', error);
      showSnackbar('Network error when loading leads tables', 'error');
    }
  };

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token available for API request');
        handleTokenExpiration();
        return;
      }
      
      console.log('Fetching agents with token present');
      
      const response = await fetch(`${API_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Agents response status:', response.status);
      
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed - token expired or invalid');
        handleTokenExpiration();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agents API response:', data);
        
        // Handle different response formats
        let agentsData = [];
        if (Array.isArray(data)) {
          agentsData = data.filter(agent => agent.agent_type === 'outbound' || agent.type === 'outbound');
        } else if (data.agents && Array.isArray(data.agents)) {
          agentsData = data.agents.filter(agent => agent.agent_type === 'outbound' || agent.type === 'outbound');
        } else {
          console.warn('Unexpected API response format for agents:', data);
          agentsData = [];
        }
        
        console.log('Filtered outbound agents for campaigns:', agentsData);
        setAgents(agentsData);
        
        if (agentsData.length === 0) {
          showSnackbar('No outbound agents found. Please create one first.', 'warning');
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to fetch agents:', response.status, errorData);
        showSnackbar('Failed to fetch agents', 'error');
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      showSnackbar('Network error when loading agents', 'error');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim() || !selectedTable || !selectedAgent || !startTime) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Ensure we have valid Date objects and validate them
      let startTimeDate;
      let formattedStartTime;
      let formattedEndTime = null;
      
      try {
        // Make sure we have a valid date object - handle both string and Date inputs
        startTimeDate = startTime instanceof Date ? startTime : new Date(startTime);
        
        // Validate the date is not Invalid Date
        if (isNaN(startTimeDate.getTime())) {
          throw new Error('Invalid start date');
        }
        
        // TIMEZONE HANDLING: Since the server uses the same timezone as the client (CET),
        // we'll use local time directly instead of converting to UTC
        
        // Format local time for API
        const localYear = startTimeDate.getFullYear();
        const localMonth = String(startTimeDate.getMonth() + 1).padStart(2, '0');
        const localDay = String(startTimeDate.getDate()).padStart(2, '0');
        const localHours = String(startTimeDate.getHours()).padStart(2, '0');
        const localMinutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const localSeconds = String(startTimeDate.getSeconds()).padStart(2, '0');
        
        // Format as YYYY-MM-DDTHH:MM:SS (ISO format without timezone indicator or milliseconds)
        formattedStartTime = `${localYear}-${localMonth}-${localDay}T${localHours}:${localMinutes}:${localSeconds}`;
        console.log(`Using local time for API (no UTC conversion): ${formattedStartTime}`);
        
        // Handle end time if provided
        if (endTime) {
          const endTimeDate = endTime instanceof Date ? endTime : new Date(endTime);
          if (!isNaN(endTimeDate.getTime())) {
            // Format local end time
            const endLocalYear = endTimeDate.getFullYear();
            const endLocalMonth = String(endTimeDate.getMonth() + 1).padStart(2, '0');
            const endLocalDay = String(endTimeDate.getDate()).padStart(2, '0');
            const endLocalHours = String(endTimeDate.getHours()).padStart(2, '0');
            const endLocalMinutes = String(endTimeDate.getMinutes()).padStart(2, '0');
            const endLocalSeconds = String(endTimeDate.getSeconds()).padStart(2, '0');
            
            formattedEndTime = `${endLocalYear}-${endLocalMonth}-${endLocalDay}T${endLocalHours}:${endLocalMinutes}:${endLocalSeconds}`;
            console.log(`End time in local format for API: ${formattedEndTime}`);
          }
        }
      } catch (dateError) {
        console.error('Error formatting date:', dateError);
        showSnackbar('Invalid date format. Please select a valid date.', 'error');
        return;
      }
      
      console.log('Debug - Campaign dates:', { 
        rawStartTime: startTime,
        formattedStartTime,
        rawEndTime: endTime,
        formattedEndTime,
        localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset() / -60
      });
      
      const payload = {
        name: campaignName.trim(),
        table_id: selectedTable,
        agent_id: selectedAgent,
        start_time: formattedStartTime,
        end_time: formattedEndTime
      };
      
      console.log('Sending campaign payload:', payload);

      const response = await fetch(`${API_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Campaign creation response:', response.status, responseText);

      if (response.ok) {
        // Parse JSON only for successful responses
        const data = JSON.parse(responseText);
        setCampaigns([...campaigns, data]);
        resetForm();
        setCampaignDialog(false);
        showSnackbar('Campaign created successfully', 'success');
      } else {
        // Try to parse error as JSON if possible
        let errorMessage = 'Failed to create campaign';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.message || 'Invalid start time format. Use ISO format (YYYY-MM-DDTHH:MM:SS.sssZ)';
          console.error('Campaign creation error:', errorData);
        } catch (e) {
          // If not JSON, use the raw response text
          errorMessage = responseText || errorMessage;
        }
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      showSnackbar('Error creating campaign', 'error');
    }
  };

  const handleUpdateCampaign = async () => {
    if (!editCampaign || !campaignName.trim()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Initialize payload with campaign name
      const payload = {
        name: campaignName.trim()
      };

      // Only update agent if selected
      if (selectedAgent) {
        payload.agent_id = selectedAgent;
      }
      
      // Handle start time if present
      if (startTime) {
        try {
          // Make sure we have a valid date object
          const startTimeDate = startTime instanceof Date ? startTime : new Date(startTime);
          
          // Validate the date is not Invalid Date
          if (isNaN(startTimeDate.getTime())) {
            throw new Error('Invalid start date');
          }
          
          // TIMEZONE HANDLING: Since server and client share timezone (CET),
          // we'll use local time directly
          
          // Format local time for API
          const localYear = startTimeDate.getFullYear();
          const localMonth = String(startTimeDate.getMonth() + 1).padStart(2, '0');
          const localDay = String(startTimeDate.getDate()).padStart(2, '0');
          const localHours = String(startTimeDate.getHours()).padStart(2, '0');
          const localMinutes = String(startTimeDate.getMinutes()).padStart(2, '0');
          const localSeconds = String(startTimeDate.getSeconds()).padStart(2, '0');
          
          // Format as YYYY-MM-DDTHH:MM:SS (ISO format without timezone indicator)
          payload.start_time = `${localYear}-${localMonth}-${localDay}T${localHours}:${localMinutes}:${localSeconds}`;
          console.log(`Using local time for API (no UTC conversion): ${payload.start_time}`);
        } catch (dateError) {
          console.error('Error formatting start date:', dateError);
          showSnackbar('Invalid start date format. Please select a valid date.', 'error');
          return;
        }
      }
      
      // Handle end time if present
      if (endTime) {
        try {
          // Make sure we have a valid date object
          const endTimeDate = endTime instanceof Date ? endTime : new Date(endTime);
          
          // Validate the date is not Invalid Date
          if (isNaN(endTimeDate.getTime())) {
            throw new Error('Invalid end date');
          }
          
          // Format local time instead of UTC
          const endLocalYear = endTimeDate.getFullYear();
          const endLocalMonth = String(endTimeDate.getMonth() + 1).padStart(2, '0');
          const endLocalDay = String(endTimeDate.getDate()).padStart(2, '0');
          const endLocalHours = String(endTimeDate.getHours()).padStart(2, '0');
          const endLocalMinutes = String(endTimeDate.getMinutes()).padStart(2, '0');
          const endLocalSeconds = String(endTimeDate.getSeconds()).padStart(2, '0');
          
          // Format as YYYY-MM-DDTHH:MM:SS (ISO format without timezone indicator)
          payload.end_time = `${endLocalYear}-${endLocalMonth}-${endLocalDay}T${endLocalHours}:${endLocalMinutes}:${endLocalSeconds}`;
        } catch (dateError) {
          console.error('Error formatting end date:', dateError);
          showSnackbar('Invalid end date format. Please select a valid date.', 'error');
          return;
        }
      }
      
      console.log('Updating campaign with payload:', payload);

      const response = await fetch(`${API_URL}/api/campaigns/${editCampaign.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Campaign update response:', response.status, responseText);

      if (response.ok) {
        // Parse JSON only for successful responses
        const updatedCampaign = JSON.parse(responseText);
        setCampaigns(campaigns.map(c => c.id === updatedCampaign.id ? updatedCampaign : c));
        resetForm();
        setCampaignDialog(false);
        showSnackbar('Campaign updated successfully', 'success');
      } else {
        // Try to parse error as JSON if possible
        let errorMessage = 'Failed to update campaign';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.message || 'Invalid start time format. Use ISO format (YYYY-MM-DDTHH:MM:SS.sssZ)';
          console.error('Campaign update error:', errorData);
        } catch (e) {
          // If not JSON, use the raw response text
          errorMessage = responseText || errorMessage;
        }
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      showSnackbar('Error updating campaign', 'error');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setCampaigns(campaigns.filter(c => c.id !== campaignId));
        showSnackbar('Campaign deleted successfully', 'success');
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to delete campaign', 'error');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showSnackbar('Error deleting campaign', 'error');
    }
  };

  const handleUpdateCampaignStatus = async (campaignId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      console.log(`Updating campaign ${campaignId} status to: ${newStatus}`);
      
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const responseText = await response.text();
      console.log(`Campaign status update response:`, response.status, responseText);

      if (response.ok) {
        try {
          const updatedCampaign = JSON.parse(responseText);
        setCampaigns(campaigns.map(c => c.id === updatedCampaign.id ? updatedCampaign : c));
          showSnackbar(`Campaign ${newStatus} successfully`, 'success');
        } catch (parseError) {
          console.error('Error parsing campaign update response:', parseError);
          // If we can't parse the response but the status was OK, still show success
        showSnackbar(`Campaign ${newStatus}`, 'success');
          // Refresh the campaign list to get the latest state
          fetchCampaigns();
        }
      } else {
        let errorMessage = `Failed to update campaign status to ${newStatus}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error updating campaign status:', error);
      showSnackbar('Error updating campaign status', 'error');
    }
  };

  const handleOpenEditDialog = (campaign) => {
    setEditCampaign(campaign);
    setCampaignName(campaign.name);
    setSelectedTable(campaign.table_id);
    setSelectedAgent(campaign.agent_id);
    setStartTime(new Date(campaign.start_time));
    setEndTime(campaign.end_time ? new Date(campaign.end_time) : null);
    setCampaignDialog(true);
  };

  const resetForm = () => {
    setEditCampaign(null);
    setCampaignName('');
    setSelectedTable('');
    setSelectedAgent('');
    setStartTime(new Date());
    setEndTime(null);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusChip = (status) => {
    let color = 'default';
    switch (status) {
      case 'pending':
        color = 'warning';
        break;
      case 'active':
        color = 'success';
        break;
      case 'completed':
        color = 'info';
        break;
      case 'paused':
        color = 'default';
        break;
      default:
        color = 'default';
    }
    return <Chip label={status} color={color} size="small" />;
  };

  const formatDate = (dateString) => {
    // Create date object from string
    const date = new Date(dateString);
    
    // Format the date with timezone information
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formattedDate = date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
    
    return formattedDate;
  };

  // Add this function to handle token expiration
  const handleTokenExpiration = () => {
    // Clear the invalid token
    localStorage.removeItem('token');
    
    // Show message to user
    showSnackbar('Your session has expired. Please log in again.', 'error');
    
    // Redirect to login page after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 4, pb: 2 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4" component="h1" gutterBottom>
                Call Campaigns
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setCampaignDialog(true);
                }}
              >
                New Campaign
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Campaigns List */}
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Scheduled Campaigns
              {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCampaigns}
            >
              Refresh
            </Button>
          </Box>

          {campaigns.length === 0 ? (
            <Alert severity="info">
              No campaigns found. Create your first campaign to schedule calls to your leads.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                All times are in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
                The server uses the same timezone for scheduling campaigns.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Lead Table</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map(campaign => (
                      <TableRow key={campaign.id} hover>
                        <TableCell>{campaign.name}</TableCell>
                        <TableCell>{getStatusChip(campaign.status)}</TableCell>
                        <TableCell>{campaign.table_name}</TableCell>
                        <TableCell>{campaign.agent_name}</TableCell>
                        <TableCell>
                          <Tooltip title={`Server UTC time: ${campaign.start_time}`}>
                            <span>{formatDate(campaign.start_time)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {campaign.end_time ? (
                            <Tooltip title={`Server UTC time: ${campaign.end_time}`}>
                              <span>{formatDate(campaign.end_time)}</span>
                            </Tooltip>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit Campaign">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenEditDialog(campaign)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          
                          {campaign.status === 'pending' && (
                            <Tooltip title="Start Now">
                              <IconButton
                                color="success"
                                onClick={() => handleUpdateCampaignStatus(campaign.id, 'active')}
                              >
                                <StartIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {campaign.status === 'active' && (
                            <Tooltip title="Pause Campaign">
                              <IconButton
                                color="warning"
                                onClick={() => handleUpdateCampaignStatus(campaign.id, 'paused')}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {campaign.status === 'paused' && (
                            <Tooltip title="Resume Campaign">
                              <IconButton
                                color="success"
                                onClick={() => handleUpdateCampaignStatus(campaign.id, 'active')}
                              >
                                <StartIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Delete Campaign">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>

        {/* Create/Edit Campaign Dialog */}
        <Dialog 
          open={campaignDialog} 
          onClose={() => setCampaignDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Campaign Name"
              fullWidth
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              required
            />
            
            <FormControl fullWidth margin="normal" disabled={!!editCampaign}>
              <InputLabel id="lead-table-label">Lead Table</InputLabel>
              <Select
                labelId="lead-table-label"
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                label="Lead Table"
                required
              >
                {leadsTables.map(table => (
                  <MenuItem key={table.id} value={table.id}>
                    {table.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="agent-label">Outbound Agent</InputLabel>
              <Select
                labelId="agent-label"
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                label="Outbound Agent"
                required
              >
                {agents.length === 0 ? (
                  <MenuItem disabled>No outbound agents available</MenuItem>
                ) : (
                  agents.map(agent => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                Times are entered and processed in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
                The server also uses this timezone for scheduling.
              </Typography>
              <DateTimePicker
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                disablePast
                sx={{ width: '100%' }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <DateTimePicker
                label="End Time (Optional)"
                value={endTime}
                onChange={setEndTime}
                disablePast
                sx={{ width: '100%' }}
              />
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              {editCampaign ? (
                'Editing a campaign will reschedule it if the campaign is pending and the start time is in the future.'
              ) : (
                'The campaign will automatically start calling leads at the specified start time. If an end time is set, it will stop at that time.'
              )}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCampaignDialog(false)}>Cancel</Button>
            <Button 
              onClick={editCampaign ? handleUpdateCampaign : handleCreateCampaign} 
              variant="contained" 
              color="primary"
              disabled={!campaignName || (!editCampaign && (!selectedTable || !selectedAgent || !startTime))}
            >
              {editCampaign ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default Campaigns; 